import { useEffect, useState } from 'react';
import { styled } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import { RootState } from '../../redux/store';
import { useAppDispatch, useAppSelector } from '../../redux/hooks';
import CloseIcon from '@mui/icons-material/Close';
import { drawerClasses } from '@mui/material';
import Grid from '@mui/material/Grid';
import { CustomerInfo } from 'orderme-api-integration-client';
import { roundDoubleNumbers } from '../../helpers/currency';
import { LogoutConfirmation } from '../auth/logout/logoutConfirmation';
import { useTranslation } from 'react-i18next';
import { PasswordChange } from '../auth/accountSettings/passwordChange';
import { closeMenu } from '../../redux/reducers/sideBarReducer';
import QRCodeImage from '../../logo/app-barcode.png';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useRouteNavigator } from '../../routes/useRouteNavigator';
import { RouteConfig } from '../../routes/routeConfig';
import {
  AboutUsRoute,
  AccountSettingsRoute,
  AchievementsRoute,
  CartRoute,
  CreditsRoute,
  FaqRoute,
  LoginRoute,
  NewsRoute,
  OrderHistoryRoute,
  PartnersRoute,
  PrivacyPolicyRoute,
  RegistrationRoute,
  RulesRoute,
  SalesPointsRoute,
  SearchRoute,
  SupportRoute,
} from '../../routes/allRoutes';
import { MenuDivider } from './menuStyle';
import { IsSewingCompany } from '../../helpers/clientConfigs';

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  [`& .${drawerClasses.paper}`]: {
    boxSizing: 'border-box',
    width: '100%',
    maxWidth: '400px',
    color: theme.palette.menu.color,
    backgroundColor: theme.palette.menu.background,
  },
}));

export default function MenuDrawer() {
  const dispatch = useAppDispatch();
  const isMenuOpened: boolean = useAppSelector((state: RootState) => state.sideBarState.isMenuOpened);
  const [open, setOpen] = useState(false);
  const routeNavigator = useRouteNavigator();
  const isUserLoggedIn: boolean = useAppSelector((state: RootState) => state.authState.isLoggedIn);
  const anonymousLogin = useAppSelector((state: RootState) => state.authState.anonymousLoginInfo);
  const isPasswordRestored: boolean = useAppSelector((state: RootState) => state.authState.isPasswordRestored);
  const customerInfo: CustomerInfo = useAppSelector((state: RootState) => state.customerState.customerInfo);
  const [openLogoutConfirmation, setOpenLogoutConfirmation] = useState(false);
  const [openPasswordChange, setOpenPasswordChange] = useState(false);
  const { t } = useTranslation();
  const smallSize = useMediaQuery('(max-width:1024px)');

  useEffect(() => {
    setOpen(isMenuOpened);
  }, [isMenuOpened]);

  const handleDrawerClose = () => {
    dispatch(closeMenu());
  };

  const navigateAndClose = (to: RouteConfig) => {
    handleDrawerClose();
    routeNavigator.goTo(to);
  };

  const logout = () => {
    setOpenLogoutConfirmation(true);
    handleDrawerClose();
  };

  const closeLogoutConfirmation = () => {
    setOpenLogoutConfirmation(false);
  };

  const showPasswordChange = () => {
    handleDrawerClose();
    setOpenPasswordChange(true);
  };

  const closePasswordChange = () => {
    setOpenPasswordChange(false);
  };

  const downloadApp = () => {
    window.location.href = 'https://onelink.to/5htyta';
  };

  return (
    <Box>
      <StyledDrawer
        variant="temporary"
        open={open}
        onClose={handleDrawerClose}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
          disableScrollLock: true,
        }}
      >
        <Box sx={{ textTransform: 'uppercase' }}>
          <Box sx={{ textAlign: 'right' }}>
            <IconButton onClick={handleDrawerClose}>
              <CloseIcon sx={{ color: 'menu.color' }} />
            </IconButton>
          </Box>
          <Divider sx={{ borderColor: 'menu.color' }} />
          <List
            sx={{
              '.MuiListItemButton-root': {
                py: '0px',
                pl: '25px',
              },
            }}
          >
            {isUserLoggedIn && (
              <>
                <ListItem>
                  <Typography sx={{ fontWeight: 'bold', width: '100%', pl: '9px' }}>
                    {customerInfo.name} {roundDoubleNumbers(customerInfo.credit)} €
                  </Typography>
                </ListItem>
                <MenuDivider />
              </>
            )}
            {anonymousLogin?.isLoggedIn && (
              <>
                <ListItem>
                  <Typography sx={{ fontWeight: 'bold', width: '100%', pl: '9px' }}>{anonymousLogin.phone}</Typography>
                </ListItem>
                <MenuDivider />
              </>
            )}
            {isUserLoggedIn && (
              <ListItemButton disabled={isPasswordRestored}>
                <ListItemText
                  onClick={() => {
                    navigateAndClose(OrderHistoryRoute);
                  }}
                  primary={t('account:ordersHistory')}
                />
              </ListItemButton>
            )}
            {isUserLoggedIn && (
              <ListItemButton>
                <ListItemText
                  onClick={() => {
                    navigateAndClose(CartRoute);
                  }}
                  primary={t('account:yourCart')}
                />
              </ListItemButton>
            )}
            {isUserLoggedIn && isPasswordRestored && (
              <ListItemButton>
                <ListItemText
                  slotProps={{
                    primary: {
                      fontWeight: 'bold',
                    },
                  }}
                  onClick={() => {
                    showPasswordChange();
                  }}
                  primary={t('account:changePassword')}
                />
              </ListItemButton>
            )}
            {isUserLoggedIn && (
              <ListItemButton disabled={isPasswordRestored}>
                <ListItemText
                  onClick={() => {
                    navigateAndClose(AccountSettingsRoute);
                  }}
                  primary={t('account:settings')}
                />
              </ListItemButton>
            )}
            {!isUserLoggedIn && (
              <>
                <ListItemButton>
                  <ListItemText
                    onClick={() => {
                      navigateAndClose(LoginRoute);
                    }}
                    primary={t('account:login')}
                  />
                </ListItemButton>
                <ListItemButton>
                  <ListItemText
                    onClick={() => {
                      navigateAndClose(RegistrationRoute);
                    }}
                    primary={t('account:registration')}
                  />
                </ListItemButton>
              </>
            )}
            <MenuDivider />
            <ListItemButton>
              <ListItemText
                onClick={() => {
                  navigateAndClose(NewsRoute);
                }}
                primary={t('news:menuTitle')}
              />
            </ListItemButton>
            <ListItemButton>
              <ListItemText
                onClick={() => {
                  navigateAndClose(SearchRoute);
                }}
                primary={t('info:allServices')}
              />
            </ListItemButton>
            <ListItemButton>
              <ListItemText
                onClick={() => {
                  navigateAndClose(CreditsRoute);
                }}
                primary={t('account:creditsAndCoupons')}
              />
            </ListItemButton>
            <ListItemButton>
              <ListItemText
                onClick={() => {
                  navigateAndClose(SalesPointsRoute);
                }}
                primary={t('salesPoint:salesPoints')}
              />
            </ListItemButton>
            {!IsSewingCompany && (
              <>
                <MenuDivider />
                <ListItemButton>
                  <ListItemText
                    onClick={() => {
                      navigateAndClose(AboutUsRoute);
                    }}
                    primary={t('info:aboutUs')}
                  />
                </ListItemButton>
                <ListItemButton>
                  <ListItemText
                    onClick={() => {
                      navigateAndClose(AchievementsRoute);
                    }}
                    primary={t('achievement:achievements')}
                  />
                </ListItemButton>
                <ListItemButton>
                  <ListItemText
                    onClick={() => {
                      navigateAndClose(PartnersRoute);
                    }}
                    primary={t('info:partners')}
                  />
                </ListItemButton>
              </>
            )}
            <MenuDivider />
            {IsSewingCompany && (
              <ListItemButton>
                <ListItemText
                  onClick={() => {
                    navigateAndClose(AboutUsRoute);
                  }}
                  primary={t('info:aboutUs')}
                />
              </ListItemButton>
            )}
            <ListItemButton>
              <ListItemText
                onClick={() => {
                  navigateAndClose(SupportRoute);
                }}
                primary={t('info:haveQuestions')}
              />
            </ListItemButton>
            <ListItemButton>
              <ListItemText
                onClick={() => {
                  navigateAndClose(FaqRoute);
                }}
                primary={t('faq:menuTitle')}
              />
            </ListItemButton>
            <ListItemButton>
              <ListItemText
                onClick={() => {
                  navigateAndClose(RulesRoute);
                }}
                primary={t('info:rules')}
              />
            </ListItemButton>
            <ListItemButton>
              <ListItemText
                onClick={() => {
                  navigateAndClose(PrivacyPolicyRoute);
                }}
                primary={t('info:privacyPolicy')}
              />
            </ListItemButton>
            <MenuDivider />
            {/* <ListItemButton>
                        <ListItemText onClick={() => { navigateAndClose("/home") }} primary={t('info:faq')} />
                    </ListItemButton> */}
            {(isUserLoggedIn || anonymousLogin.isLoggedIn) && (
              <>
                <ListItemButton sx={{ mt: '20px' }}>
                  <ListItemText
                    onClick={() => {
                      logout();
                    }}
                    primary={t('account:logout')}
                  />
                </ListItemButton>
              </>
            )}

            {!IsSewingCompany && (
              <Box sx={{ mt: '30px', pl: '25px' }}>
                {smallSize ? (
                  <ListItemText
                    sx={{ mt: '30px' }}
                    slotProps={{
                      primary: {
                        border: '2px solid',
                        display: 'inline-block',
                        borderRadius: '15px',
                        py: '10px',
                        px: '15px',
                      },
                    }}
                    onClick={() => {
                      downloadApp();
                    }}
                    primary={t('info:downloadApp')}
                  />
                ) : (
                  <Grid
                    container
                    sx={{
                      ml: '0px',
                      width: '100%',
                      justifyContent: 'center',
                      alignItems: 'center',
                    }}
                  >
                    <Grid size="auto">
                      <img alt="mobile-app-qr-code" src={QRCodeImage} width={120} height={120} />
                    </Grid>
                    <Grid size="grow" sx={{ mx: '25px' }}>
                      <ListItemText primary={t('info:downloadApp')} />
                    </Grid>
                  </Grid>
                )}
              </Box>
            )}
          </List>
        </Box>
      </StyledDrawer>
      <LogoutConfirmation open={openLogoutConfirmation} onClose={closeLogoutConfirmation} />
      <PasswordChange open={openPasswordChange} onClose={closePasswordChange} />
    </Box>
  );
}
